/**
 * Адаптер до `MyTelegram.AdminServer` (`/api/v1/admin/*`).
 *
 * Тримає ТІЛЬКИ те, що бекенд реально читає з існуючих MongoDB read-моделей
 * MyTelegram (користувачі/групи-канали/боти/статистика) + власну автентифікацію
 * адмінки. Усе інше (фінанси/маркетплейс/модерація/аналітика/RBAC на 16 ролей
 * тощо) було видалено разом з відповідними екранами — жодного підкріплення в
 * бекенді ці домени не мали.
 */

import { api } from './api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

/* ── Типи (реальні відповіді бекенду) ─────────────────────────── */

export interface AdminStats {
  users: number
  channels: number
  bots: number
}

export interface AdminUserRow {
  id: number
  first_name?: string | null
  last_name?: string | null
  username?: string | null
  phone?: string | null
  status: string
  /** Epoch ms. */
  created_at?: string | number | null
  last_seen?: string | number | null
  is_banned: boolean
  is_frozen: boolean
  label?: string | null
}

/* ── Локальні допоміжні (клієнтські фільтр/сортування/пагінація —
   бекенд наразі не підтримує offset/total на /admin/users, /admin/groups,
   /admin/bots) ─────────────────────────────────────────────────── */

/**
 * LIVE BUG FIX (user-reported, 2026-09-06, "користувачі і боти відображаються не всі"):
 * fetchUsers/fetchBots used to request only `params.pageSize` (50 by default) rows from the
 * backend and return them as a single, un-paginated page - every other list adapter in this file
 * instead fetches a large batch once and calls paginate() locally (see this section's own comment
 * above and fetchUserSessions etc. below). With only page 1 ever actually fetched and no
 * offset/total sent to or returned from the backend, the DataTable's "next page" control just
 * re-requested the identical first N rows forever - any user/bot past the first pageSize was
 * unreachable. 209 real users / 5 non-deleted bots exist today; this cap comfortably covers a
 * self-hosted deployment at this scale without needing real server-side pagination.
 */
const FETCH_ALL_LIMIT = 1000

function field(row: unknown, key: string): unknown {
  return (row as Record<string, unknown>)[key]
}

function matchesFilters<T>(row: T, params: FetchParams): boolean {
  return params.filters.every((f) => {
    const v = field(row, f.key)
    switch (f.op) {
      case 'contains':
        return String(v ?? '').toLowerCase().includes(String(f.value).toLowerCase())
      case 'equals':
        return String(v ?? '') === String(f.value)
      case 'startsWith':
        return String(v ?? '').toLowerCase().startsWith(String(f.value).toLowerCase())
      case 'is':
        return Boolean(v) === Boolean(f.value)
      case 'in':
        return Array.isArray(f.value) && f.value.map(String).includes(String(v))
      case 'between': {
        if (!Array.isArray(f.value)) return true
        const [a, b] = f.value
        const n = typeof v === 'number' ? v : Number(v)
        if (a !== '' && a !== undefined && n < Number(a)) return false
        if (b !== '' && b !== undefined && n > Number(b)) return false
        return true
      }
      default:
        return true
    }
  })
}

function sortLocally<T>(rows: T[], params: FetchParams): T[] {
  const spec = params.sort[0]
  if (!spec) return rows
  const dir = spec.desc ? -1 : 1
  return [...rows].sort((x, y) => {
    const a = field(x, spec.key)
    const b = field(y, spec.key)
    if (a === b) return 0
    if (a === null || a === undefined) return 1
    if (b === null || b === undefined) return -1
    if (typeof a === 'number' && typeof b === 'number') return (a - b) * dir
    return String(a).localeCompare(String(b)) * dir
  })
}

function paginate<T>(rows: T[], params: FetchParams): FetchResult<T> {
  const start = (params.page - 1) * params.pageSize
  return { rows: rows.slice(start, start + params.pageSize), total: rows.length }
}

/* ── A3.01 Користувачі ────────────────────────────────────────── */

export async function fetchUsers(params: FetchParams): Promise<FetchResult<AdminUserRow>> {
  const data = await api.get<{ users: AdminUserRow[] }>('/admin/users', {
    query: { limit: FETCH_ALL_LIMIT, q: params.search || undefined },
    signal: params.signal,
  })
  const rows = sortLocally(
    (data.users ?? []).filter((r) => matchesFilters(r, params)),
    params,
  )
  return paginate(rows, params)
}

/** A3.07 — картка користувача (GET /admin/users/:id). */
export interface AdminUserDetail {
  id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  phone?: string | null
  bio?: string | null
  status: string
  is_bot: boolean
  is_official: boolean
  user_number?: string | null
  /** epoch ms */
  created_at: number | null
  last_seen?: number | null
  is_banned: boolean
  ban_reason?: string | null
  is_frozen: boolean
  freeze_reason?: string | null
  label?: string | null
}
export async function fetchUser(id: number, signal?: AbortSignal): Promise<AdminUserDetail | null> {
  const data = await api.get<{ user: AdminUserDetail }>(`/admin/users/${id}`, signal ? { signal } : {})
  return data.user ?? null
}

/** A3.10 — пристрій/сесія користувача (хеш токена НЕ повертається). */
export interface AdminSession {
  id: number
  platform?: string | null
  device_model?: string | null
  os_version?: string | null
  app_version?: string | null
  last_ip?: string | null
  last_active?: number | null
  created_at: number
  revoked_at?: number | null
  active: boolean
  two_factor_pending: boolean
}
export async function fetchUserSessions(id: number, params: FetchParams): Promise<FetchResult<AdminSession>> {
  const data = await api.get<{ sessions: AdminSession[] }>(`/admin/users/${id}/sessions`, {
    signal: params.signal,
  })
  const rows = (data.sessions ?? []).filter((s) => matchesFilters(s, params))
  return paginate(sortLocally(rows, params), params)
}

/** A3.11 — групи/канали, учасником яких є користувач (GET /admin/users/:id/groups). */
export interface AdminUserGroup {
  id: number
  type: string
  title?: string | null
  username?: string | null
  role: string
  status: string
  member_count: number
  joined_at?: number | null
}
export async function fetchUserGroups(id: number, params: FetchParams): Promise<FetchResult<AdminUserGroup>> {
  const data = await api.get<{ groups: AdminUserGroup[] }>(`/admin/users/${id}/groups`, {
    signal: params.signal,
  })
  const rows = (data.groups ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A3.13 — групи/канали у власності: той самий ендпоінт, лише role='owner'. */
export async function fetchUserOwnedGroups(
  id: number,
  params: FetchParams,
): Promise<FetchResult<AdminUserGroup>> {
  const data = await api.get<{ groups: AdminUserGroup[] }>(`/admin/users/${id}/groups`, {
    signal: params.signal,
  })
  const rows = (data.groups ?? []).filter((x) => x.role === 'owner').filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/** A3.14 — боти у власності користувача (GET /admin/users/:id/bots). */
export interface AdminOwnedBot {
  id: number
  user_id: number
  username: string
  name: string
  created_at: number | null
  revoked_at: number | null
  revoked: boolean
}
export async function fetchUserBots(id: number, params: FetchParams): Promise<FetchResult<AdminOwnedBot>> {
  const data = await api.get<{ bots: AdminOwnedBot[] }>(`/admin/users/${id}/bots`, {
    signal: params.signal,
  })
  const rows = (data.bots ?? []).filter((x) => matchesFilters(x, params))
  return paginate(sortLocally(rows, params), params)
}

/* ── A4 Групи/канали ──────────────────────────────────────────── */

/** A4.01 — список груп/каналів (GET /admin/groups). */
export interface AdminGroupRow {
  id: number
  type: string
  title?: string | null
  username?: string | null
  member_count: number
  is_forum: boolean
  owner_id?: number | null
  created_at: number
  verified: boolean
  is_banned: boolean
  label?: string | null
}
export async function fetchGroups(params: FetchParams): Promise<FetchResult<AdminGroupRow>> {
  const data = await api.get<{ groups: AdminGroupRow[] }>('/admin/groups', {
    query: { limit: params.pageSize, q: params.search || undefined },
    signal: params.signal,
  })
  const rows = sortLocally(
    (data.groups ?? []).filter((r) => matchesFilters(r, params)),
    params,
  )
  return { rows }
}

/** A4.04 — картка групи/каналу (GET /admin/groups/:id). */
export interface AdminGroupDetail {
  id: number
  type: string
  title?: string | null
  username?: string | null
  about?: string | null
  member_count: number
  is_forum: boolean
  owner_id?: number | null
  owner_username?: string | null
  created_at: number
  verified: boolean
  is_banned: boolean
  ban_reason?: string | null
  label?: string | null
}
export async function fetchGroup(id: number, signal?: AbortSignal): Promise<AdminGroupDetail | null> {
  const data = await api.get<{ group: AdminGroupDetail }>(`/admin/groups/${id}`, signal ? { signal } : {})
  return data.group ?? null
}

/** A4.05 — учасник групи/каналу (GET /admin/groups/:id/members). */
export interface AdminMember {
  user_id: number
  username?: string | null
  first_name?: string | null
  last_name?: string | null
  role: string
  custom_title?: string | null
  joined_at?: number | null
  restricted: boolean
  is_bot?: boolean
}
export async function fetchGroupMembers(id: number, params: FetchParams): Promise<FetchResult<AdminMember>> {
  const data = await api.get<{ members: AdminMember[] }>(`/admin/groups/${id}/members`, {
    signal: params.signal,
  })
  const rows = (data.members ?? []).filter((m) => matchesFilters(m, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.06 — адміни: той самий ендпоінт, лише роль owner/admin. */
export async function fetchGroupAdmins(id: number, params: FetchParams): Promise<FetchResult<AdminMember>> {
  const data = await api.get<{ members: AdminMember[] }>(`/admin/groups/${id}/admins`, {
    signal: params.signal,
  })
  const rows = (data.members ?? []).filter((m) => matchesFilters(m, params))
  return paginate(sortLocally(rows, params), params)
}

/** A4.12 — підключені боти: серверний is_bot-фільтр. */
export async function fetchGroupBots(id: number, params: FetchParams): Promise<FetchResult<AdminMember>> {
  const data = await api.get<{ members: AdminMember[] }>(`/admin/groups/${id}/bots`, {
    signal: params.signal,
  })
  const rows = (data.members ?? []).filter((m) => matchesFilters(m, params))
  return paginate(sortLocally(rows, params), params)
}

/* ── A5 Боти ──────────────────────────────────────────────────── */

/** A5.01 — список ботів (GET /admin/bots). */
export interface AdminBotRow {
  id: number
  username?: string | null
  first_name?: string | null
  is_official: boolean
  created_at: number | null
  last_seen?: number | null
}
export async function fetchBots(params: FetchParams): Promise<FetchResult<AdminBotRow>> {
  const data = await api.get<{ bots: AdminBotRow[] }>('/admin/bots', {
    query: { limit: FETCH_ALL_LIMIT, q: params.search || undefined },
    signal: params.signal,
  })
  const rows = sortLocally(
    (data.bots ?? []).filter((r) => matchesFilters(r, params)),
    params,
  )
  return paginate(rows, params)
}

/* ── A2.01 Дашборд ────────────────────────────────────────────── */

export async function fetchStats(signal?: AbortSignal): Promise<AdminStats> {
  const data = await api.get<{ stats: AdminStats }>('/admin/stats', signal ? { signal } : {})
  return data.stats
}

/* ── A1 Автентифікація адмінки ────────────────────────────────── */

export interface AdminMe {
  id: number
  username: string
  role: string
  roles: string[]
  permissions: string[]
}
export async function fetchMe(signal?: AbortSignal): Promise<AdminMe | null> {
  try {
    const data = await api.get<AdminMe>('/admin/me', signal ? { signal } : {})
    return data ?? null
  } catch {
    return null
  }
}

/* ── Модерація (реальні дії через EventFlow command bus у AdminServer) ──── */

export function banUser(id: number, reason: string): Promise<unknown> {
  return api.post(`/admin/users/${id}/ban`, { reason })
}
export function unbanUser(id: number): Promise<unknown> {
  return api.post(`/admin/users/${id}/unban`)
}
export function freezeUser(id: number, reason: string): Promise<unknown> {
  return api.post(`/admin/users/${id}/freeze`, { reason })
}
export function unfreezeUser(id: number): Promise<unknown> {
  return api.post(`/admin/users/${id}/unfreeze`)
}
export function verifyUser(id: number, verified: boolean): Promise<unknown> {
  return api.post(`/admin/users/${id}/verify`, { verified })
}
export type ModerationLabel = 'scam' | 'fake' | 'fraud' | 'spam'
export function labelUser(id: number, label: ModerationLabel): Promise<unknown> {
  return api.post(`/admin/users/${id}/label`, { label })
}
export function clearUserLabel(id: number): Promise<unknown> {
  return api.post(`/admin/users/${id}/label/clear`)
}
/** Real, permanent deletion - not ban/freeze. Photo/about/name scrubbed, username freed for reuse,
 * excluded from search, sessions terminated. Matches real Telegram's account.deleteAccount. */
export function deleteUser(id: number): Promise<unknown> {
  return api.post(`/admin/users/${id}/delete`)
}

export function banGroup(id: number, reason: string): Promise<unknown> {
  return api.post(`/admin/groups/${id}/ban`, { reason })
}
export function unbanGroup(id: number): Promise<unknown> {
  return api.post(`/admin/groups/${id}/unban`)
}
export function verifyGroup(id: number, verified: boolean): Promise<unknown> {
  return api.post(`/admin/groups/${id}/verify`, { verified })
}
export function labelGroup(id: number, label: ModerationLabel): Promise<unknown> {
  return api.post(`/admin/groups/${id}/label`, { label })
}
export function clearGroupLabel(id: number): Promise<unknown> {
  return api.post(`/admin/groups/${id}/label/clear`)
}

export function grantBotVerifier(
  id: number,
  body: { icon: number; company: string; custom_description?: string },
): Promise<unknown> {
  return api.post(`/admin/bots/${id}/verifier/grant`, body)
}
export function revokeBotVerifier(id: number): Promise<unknown> {
  return api.post(`/admin/bots/${id}/verifier/revoke`)
}

/* ── A6 Скарги (GET /admin/reports — реальні дані з report-RPC заявок) ──── */

export interface AdminReport {
  id: string
  reporter_user_id: number
  reporter_username?: string | null
  target_type: string
  target_peer_id: number
  target_username?: string | null
  message_id?: number | null
  reason: string
  comment?: string | null
  status: string
  created_at: number
  resolved_at?: number | null
  resolved_by_admin_id?: number | null
  resolution?: string | null
}
export async function fetchReports(params: FetchParams): Promise<FetchResult<AdminReport>> {
  const data = await api.get<{ reports: AdminReport[] }>('/admin/reports', {
    query: { limit: params.pageSize },
    signal: params.signal,
  })
  const rows = sortLocally(
    (data.reports ?? []).filter((r) => matchesFilters(r, params)),
    params,
  )
  return { rows }
}
export function resolveReport(id: string, resolution?: string): Promise<unknown> {
  return api.post(`/admin/reports/${id}/resolve`, { resolution })
}
export function dismissReport(id: string, resolution?: string): Promise<unknown> {
  return api.post(`/admin/reports/${id}/dismiss`, { resolution })
}
export function deleteReportedMessage(id: string, notice?: string): Promise<unknown> {
  return api.post(`/admin/reports/${id}/delete-message`, { notice })
}

/* ── A6b Заявки на верификацию (GET /admin/verification-requests — бот "Grom Verification") ── */

export interface AdminVerificationRequest {
  id: string
  requester_user_id: number
  requester_username?: string | null
  target_type: string
  target_username: string
  reason: string
  status: string
  created_at: number
  resolved_at?: number | null
  resolved_by_admin?: string | null
  resolution?: string | null
  proof_links?: { category: string; url: string }[] | null
}
export async function fetchVerificationRequests(params: FetchParams): Promise<FetchResult<AdminVerificationRequest>> {
  const data = await api.get<{ requests: AdminVerificationRequest[] }>('/admin/verification-requests', {
    query: { limit: params.pageSize },
    signal: params.signal,
  })
  const rows = sortLocally(
    (data.requests ?? []).filter((r) => matchesFilters(r, params)),
    params,
  )
  return { rows }
}
export function approveVerificationRequest(id: string, resolution?: string): Promise<unknown> {
  return api.post(`/admin/verification-requests/${id}/approve`, { resolution })
}
export function rejectVerificationRequest(id: string, resolution?: string): Promise<unknown> {
  return api.post(`/admin/verification-requests/${id}/reject`, { resolution })
}

/* ── A7 Підтримка (GET /admin/support — реальні тікети з бота "Grom Support") ── */

export interface AdminSupportMessage {
  /** "user" або "admin:<username>". */
  from: string
  text: string
  at: number
}
export interface AdminSupportTicket {
  id: string
  user_id: number
  username?: string | null
  status: string
  created_at: number
  closed_at?: number | null
  closed_by_admin?: string | null
  messages: AdminSupportMessage[]
}
export async function fetchSupportTickets(params: FetchParams): Promise<FetchResult<AdminSupportTicket>> {
  const data = await api.get<{ tickets: AdminSupportTicket[] }>('/admin/support', {
    query: { limit: params.pageSize },
    signal: params.signal,
  })
  const rows = sortLocally(
    (data.tickets ?? []).filter((t) => matchesFilters(t, params)),
    params,
  )
  return { rows }
}
export async function fetchSupportTicket(id: string, signal?: AbortSignal): Promise<AdminSupportTicket | null> {
  const data = await api.get<{ ticket: AdminSupportTicket }>(`/admin/support/${id}`, signal ? { signal } : {})
  return data.ticket ?? null
}
export function replySupportTicket(id: string, text: string): Promise<unknown> {
  return api.post(`/admin/support/${id}/reply`, { text })
}
export function closeSupportTicket(id: string): Promise<unknown> {
  return api.post(`/admin/support/${id}/close`)
}
export function resolveSupportAppeal(id: string, approved: boolean, resolution?: string): Promise<unknown> {
  return api.post(`/admin/support/${id}/resolve-appeal`, { approved, resolution })
}
